/**
 * KpiScreen — KPI board (CLAUDE.md screen 2). Tiles for median_time_to_guest, table_turnover,
 * foodcost_dev: value vs target, unit, health. Live updates over the `kpi:` channel with
 * polling fallback. GREEN = health (status), INK = actions.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { KpiValue } from "@/types/contract";
import { api, UnauthorizedError } from "@/api/client";
import { ScreenHeader } from "@/components/ScreenHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { KpiTile } from "@/components/kpi/KpiTile";
import { AlertIcon } from "@/components/ui/icons";
import type { ConnectionState } from "@/hooks/useFeedChannel";
import { useKpiChannel } from "@/hooks/useKpiChannel";

interface Props {
  restaurantId: string;
  onConnectionChange: (s: ConnectionState) => void;
}

type Phase =
  | { kind: "loading" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

export function KpiScreen({ restaurantId, onConnectionChange }: Props) {
  const [kpis, setKpis] = useState<KpiValue[]>([]);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });

  const load = useCallback(async () => {
    setPhase({ kind: "loading" });
    try {
      const list = await api.getKpis({ restaurant_id: restaurantId });
      setKpis(list);
      setPhase({ kind: "ready" });
    } catch (e) {
      const message =
        e instanceof UnauthorizedError
          ? "Сессия истекла — войдите в Orees Cloud снова."
          : e instanceof Error
            ? e.message
            : "Неизвестная ошибка";
      setPhase({ kind: "error", message });
    }
  }, [restaurantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const connection = useKpiChannel(restaurantId, (next) => {
    setKpis(next);
    setPhase({ kind: "ready" });
  });

  const lastConn = useRef<ConnectionState | null>(null);
  useEffect(() => {
    if (lastConn.current !== connection) {
      lastConn.current = connection;
      onConnectionChange(connection);
    }
  }, [connection, onConnectionChange]);

  return (
    <>
      <ScreenHeader
        eyebrow="Показатели смены"
        title="KPI"
        subtitle="Объективная картина для честной нагрузки и оценки"
      />

      {phase.kind === "loading" && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="flex flex-col gap-3">
              <div className="skeleton h-4 w-32 rounded-full" />
              <div className="skeleton h-9 w-24 rounded-control" />
              <div className="skeleton h-4 w-full rounded-control" />
            </Card>
          ))}
        </div>
      )}

      {phase.kind === "error" && (
        <Card className="flex flex-col items-center gap-3 py-10 text-center">
          <AlertIcon size={26} className="text-danger" />
          <h3 className="text-base font-semibold text-ink">
            Не удалось загрузить KPI
          </h3>
          <p className="max-w-sm text-sm text-muted">{phase.message}</p>
          <Button variant="secondary" size="sm" onClick={() => void load()}>
            Повторить
          </Button>
        </Card>
      )}

      {phase.kind === "ready" && kpis.length === 0 && (
        <Card className="py-10 text-center text-sm text-muted">
          За выбранный период данных KPI нет.
        </Card>
      )}

      {phase.kind === "ready" && kpis.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-live="polite">
          {kpis.map((kpi) => (
            <KpiTile key={kpi.metric} kpi={kpi} />
          ))}
        </div>
      )}
    </>
  );
}
